import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead, buildCourseSchema } from '../components/SEOHead';
import { GraduationCap, BookOpen, Award, Users, Clock, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoursesPage() {
  const { t, language } = useLanguage();
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const levelIcons = {
    parvulitos: '🌱',
    primaria_1: '📚',
    primaria_2: '📖',
    primaria_3: '✏️',
    eso_1: '🎓',
    eso_2: '📝',
    bachillerato: '🏆',
    dele: '🎯'
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Cursos de Español — Colegio ARCE | Desde Parvulitos hasta Bachillerato"
        titleEN="Spanish Courses — Colegio ARCE | From Preschool to Senior High"
        descES="Cursos de español online desde Parvulitos hasta 2º de Bachillerato, más preparación DELE. Currículo oficial del Ministerio de Educación de España. Grupos reducidos de máximo 8 estudiantes."
        descEN="Online Spanish courses from Preschool to Senior High School, plus DELE exam prep. Official Spanish Ministry of Education curriculum. Small groups of maximum 8 students."
        path="/courses"
        schema={{ "@context": "https://schema.org", "@type": "ItemList", "name": "Spanish Courses at Colegio ARCE", "itemListElement": courses.map((c, i) => ({ "@type": "ListItem", "position": i + 1, "item": { "@type": "Course", "name": c.name, "description": c.description, "provider": { "@type": "EducationalOrganization", "name": "Colegio ARCE" }, "courseMode": "online", "inLanguage": "es" } })) }}
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' },
          { nameES: 'Escolaridad', nameEN: 'Courses', path: '/courses' }
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
            {language === 'es' ? 'Escolaridad' : 'Education'}
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] mb-6" data-testid="courses-title">
            {t('courses.title')}
          </h1>
          <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
            {t('courses.subtitle')}
          </p>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="section-padding bg-white" id="methodology" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-6">
                {t('sections.methodology')}
              </h2>
              <p className="text-[#86868B] leading-relaxed mb-6">
                {language === 'es'
                  ? 'Nuestro currículo está alineado con el programa oficial del Ministerio de Educación de España, adaptado específicamente para estudiantes internacionales. Esto garantiza que los estudiantes reciban una educación de calidad reconocida internacionalmente.'
                  : 'Our curriculum is aligned with the official Spanish Ministry of Education program, specifically adapted for international students. This ensures students receive quality education recognized internationally.'}
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="h-6 w-6 text-[#E63946]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">
                      {language === 'es' ? 'Profesores Nativos' : 'Native Teachers'}
                    </h3>
                    <p className="text-sm text-[#86868B]">
                      {language === 'es' 
                        ? 'Todos nuestros profesores son nativos de España con más de 15 años de experiencia.'
                        : 'All our teachers are native Spanish speakers with over 15 years of experience.'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-[#E63946]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">
                      {language === 'es' ? 'Grupos Reducidos' : 'Small Groups'}
                    </h3>
                    <p className="text-sm text-[#86868B]">
                      {language === 'es'
                        ? 'Clases con máximo 8 estudiantes para atención personalizada.'
                        : 'Classes with maximum 8 students for personalized attention.'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#E63946]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1D1D1F]">
                      {language === 'es' ? 'Sesiones de 50 minutos' : '50-minute Sessions'}
                    </h3>
                    <p className="text-sm text-[#86868B]">
                      {language === 'es'
                        ? 'Una clase semanal, 30 semanas al año, 3 trimestres.'
                        : 'One class per week, 30 weeks per year, 3 trimesters.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding bg-[#F5F5F7]" id="philosophy" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8">
              <BookOpen className="h-12 w-12 text-[#E63946] mb-6" />
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
                {t('sections.philosophy')}
              </h2>
              <p className="text-[#86868B] leading-relaxed">
                {language === 'es'
                  ? 'En Colegio ARCE creemos en el arraigo cultural como pilar fundamental del aprendizaje del español. No solo enseñamos un idioma, transmitimos la riqueza de la cultura española a través de tradiciones, literatura, historia y valores que forman parte de nuestra identidad.'
                  : 'At Colegio ARCE we believe in cultural roots as a fundamental pillar of Spanish learning. We don\'t just teach a language, we transmit the richness of Spanish culture through traditions, literature, history and values that are part of our identity.'}
              </p>
            </div>
        </div>
      </section>

      {/* Levels */}
      <section className="section-padding bg-white" id="niveles" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-4" data-testid="levels-title">
              {t('courses.levels')}
            </h2>
            <p className="text-[#86868B]">
              {language === 'es' 
                ? 'Desde Parvulitos hasta 2º de Bachillerato'
                : 'From Preschool to Senior High School'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.filter(c => c.level !== 'dele').map(course => (
              <div 
                key={course.course_id} 
                className="card-apple p-6"
                data-testid={`course-card-${course.level}`}
              >
                <span className="text-4xl mb-4 block">{levelIcons[course.level] || '📚'}</span>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">
                  {language === 'es' ? course.name : (course.name_en || course.name)}
                </h3>
                <p className="text-sm text-[#E63946] font-medium mb-3">{course.age_range}</p>
                <p className="text-sm text-[#86868B]">
                  {language === 'es' ? course.description : (course.description_en || course.description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELE Section */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="card-apple p-12 text-center" data-testid="dele-section">
            <Award className="h-16 w-16 text-[#E63946] mx-auto mb-6" />
            <h2 className="text-3xl font-semibold text-[#1D1D1F] mb-4">
              {t('courses.dele')}
            </h2>
            <p className="text-lg text-[#86868B] mb-6 max-w-2xl mx-auto">
              {t('courses.deleDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(level => (
                <span 
                  key={level}
                  className="px-4 py-2 bg-[#F5F5F7] rounded-full text-sm font-medium text-[#1D1D1F]"
                >
                  DELE {level}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links CTA */}
      <section className="section-padding bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
            {language === 'es' ? '¿Listo para inscribirte?' : 'Ready to enroll?'}
          </h2>
          <p className="text-[#86868B] mb-8">
            {language === 'es'
              ? 'Consulta nuestros planes de precios o agenda una cita gratuita con nuestro equipo.'
              : 'Check our pricing plans or schedule a free consultation with our team.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/pricing" className="btn-primary" data-testid="courses-cta-pricing">
              {language === 'es' ? 'Ver precios y planes' : 'View pricing & plans'}
            </Link>
            <a href="https://scheduler.zoom.us/laura-pallares-nieto/informacion-colegio-arce" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="courses-cta-contact">
              {language === 'es' ? 'Agenda una cita' : 'Schedule a consultation'}
            </a>
            <Link to="/team" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="courses-cta-team">
              {language === 'es' ? 'Conocer al equipo →' : 'Meet the team →'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
